if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.section = this.closest('.js-product');
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');

      // Bind to on:variant:change event for this product
      this.section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.getVariantInventory();
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      // ERP availability is separate from supplier quantities. Native inventory
      // still limits checkout; expired supplier promises are never displayed.
      const iliria = this.getVariantInventory().iliria_availability;
      clearTimeout(this.iliriaExpiryTimer);
      if (iliria && iliria.version === 1) {
        this.section.querySelectorAll('.product-availability').forEach((notice) => { notice.hidden = true; });
        const now = Date.now();
        const supplierChecks = Array.isArray(iliria.suppliers) ? iliria.suppliers : [];
        const freshOffers = supplierChecks.filter((offer) => {
          const checked = Date.parse(offer.checked_at), until = Date.parse(offer.valid_until);
          return offer.status === 'available' && checked <= now && until > now && until > checked && until - checked <= 168 * 3600000;
        });
        const fresh = supplierChecks.length ? freshOffers.length > 0 : iliria.supplier_available === true && Date.parse(iliria.supplier_until) > now;
        // Verified Edision ERP supplier: Thessaloniki dispatch, not next-day.
        // Choose the fastest still-fresh source; own Home/Store stock wins.
        const supplierLabel = freshOffers.length && freshOffers.every((offer) => offer.id === 'bc4e0321-7596-4249-924a-f9f2430cc76e') ? '1–3 εργάσιμες' : 'Επόμενη εργάσιμη';
        const immediate = count > Math.max(0, Number(iliria.home) || 0);
        const nextDay = count > 0 || fresh;
        if (!nextDay) {
          this.section.querySelectorAll('.js-product-form-main button[name="add"]').forEach((button) => { button.disabled = true; });
          this.section.querySelectorAll('.js-product-form-main .product-info__dynamic-checkout-button').forEach((button) => { button.hidden = true; });
        }
        const label = immediate ? 'Άμεσα διαθέσιμο' : count > 0 ? 'Επόμενη εργάσιμη' : fresh ? supplierLabel : Date.parse(iliria.out_until) > now ? 'Εξαντλήθηκε' : 'Επικοινωνήστε για διαθεσιμότητα';
        this.hidden = false;
        this.setAttribute('data-inventory-level', immediate ? 'normal' : nextDay ? 'backordered' : 'none');
        this.inventoryNotice.textContent = label;
        this.inventoryNotice.style.cssText = 'font-size:15px;font-weight:700;letter-spacing:0;padding:8px 12px;border-radius:8px;display:inline-block;background:' + (immediate ? '#edf8f0;color:#146b35' : nextDay ? '#fff6e8;color:#875600' : '#f4f4f5;color:#52525b');
        if (this.urgencyMessage) this.urgencyMessage.textContent = '';
        if (this.indicatorBar) this.indicatorBar.hidden = true;
        const nextExpiry = freshOffers.length ? Math.min(...freshOffers.map((offer) => Date.parse(offer.valid_until))) : Date.parse(iliria.supplier_until);
        if (fresh) this.iliriaExpiryTimer = setTimeout(() => this.updateInventory(count, available, inventoryPolicy), Math.min(2147483647, Math.max(1000, nextExpiry - now + 1000)));
        return;
      }
      if (count === 0 && !available && this.hidden) {
        return;
      }

      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = theme.strings.backordered;
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
